// Rule-based pop-ups with their own design, separate from Compose: each one shows itself once to
// every member who trips its trigger.
import React, { useState } from 'react';
import { DEFAULT_CTA, DEFAULT_FOCUS, DELAYS, PLACEMENTS, TRIGGERS, delayLabel, formatLabel, num, placementLabel, templateError, templateFormat, triggerLabel } from './format';
import ImageField from './ImageField';
import FocusImage from './FocusImage';

const BLANK = { name: '', title: '', body: '', cta: DEFAULT_CTA, image: '', placement: 'center', imageFocus: DEFAULT_FOCUS, trigger: TRIGGERS[0].id, delayHours: 2 };

const Templates = ({ templates, onSave, onToggle, onDelete, saving, busyId }) => {
  const [draft, setDraft] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const patch = (fields) => setDraft((prev) => ({ ...prev, ...fields }));
  const problem = templateError(draft);

  const save = async () => {
    const saved = await onSave({ ...draft, format: templateFormat(draft) }, editingId);
    if (saved) {
      setDraft(BLANK);
      setEditingId(null);
    }
  };

  const edit = (template) => {
    setEditingId(template._id);
    setDraft({
      name: template.name || '',
      title: template.title || '',
      body: template.body || '',
      cta: template.cta || DEFAULT_CTA,
      image: template.image || '',
      placement: template.placement || 'center',
      imageFocus: template.imageFocus || DEFAULT_FOCUS,
      trigger: template.trigger || TRIGGERS[0].id,
      delayHours: template.delayHours ?? 0,
    });
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(BLANK);
  };

  return (
    <div className="card hist-card">
      <h3>Automation templates</h3>
      <p className="tmpl-intro">
        <b>1.</b> Write the template’s headline/content and upload its design ·{' '}
        <b>2.</b> Name it and pick the trigger + delay ·{' '}
        <b>3.</b> Save. It shows itself to each member who fires the trigger, once.
      </p>

      <label className="lab" style={{ marginTop: 0 }} htmlFor="pn-tmpl-title">Template headline</label>
      <input
        id="pn-tmpl-title"
        className="txt"
        maxLength={60}
        value={draft.title}
        onChange={(e) => patch({ title: e.target.value })}
        placeholder="e.g. Welcome to Favor"
      />

      <label className="lab" htmlFor="pn-tmpl-body">Template content</label>
      <textarea
        id="pn-tmpl-body"
        className="txt"
        maxLength={180}
        style={{ minHeight: 60 }}
        value={draft.body}
        onChange={(e) => patch({ body: e.target.value })}
        placeholder="One or two lines — the template’s own text, nothing to do with Compose above."
      />

      <label className="lab" htmlFor="pn-tmpl-cta">Button text · opens the plans screen</label>
      <input
        id="pn-tmpl-cta"
        className="txt"
        maxLength={30}
        value={draft.cta}
        onChange={(e) => patch({ cta: e.target.value })}
        placeholder={DEFAULT_CTA}
      />

      <span className="lab">Template design · optional</span>
      <ImageField
        value={draft.image}
        onChange={(url) => patch({ image: url })}
        hint="This design belongs to the template only. Image alone, text alone, or both — the format follows what you fill in."
      />

      {draft.image && (
        <>
          <div className="crop">
            <FocusImage
              src={draft.image}
              focus={draft.imageFocus}
              onChange={(imageFocus) => patch({ imageFocus })}
            />
          </div>
          <div className="img-hint" style={{ marginTop: 7 }}>
            The 16:10 the pop-up crops to — use Reposition if the crop cuts off the subject.
          </div>
        </>
      )}

      <hr className="sep" />
      <div className="tmpl-form" style={{ marginTop: 20 }}>
        <label htmlFor="pn-tmpl-name">
          Template name
          <input
            id="pn-tmpl-name"
            value={draft.name}
            onChange={(e) => patch({ name: e.target.value })}
            placeholder="e.g. Welcome pop-up — not the headline"
          />
        </label>
        <label htmlFor="pn-tmpl-trigger">
          Trigger
          <select
            id="pn-tmpl-trigger"
            value={draft.trigger}
            onChange={(e) => patch({ trigger: e.target.value })}
          >
            {TRIGGERS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </label>
        <label htmlFor="pn-tmpl-placement">
          Placement
          <select
            id="pn-tmpl-placement"
            value={draft.placement}
            onChange={(e) => patch({ placement: e.target.value })}
          >
            {PLACEMENTS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </label>
        <label htmlFor="pn-tmpl-delay">
          Delay
          <select
            id="pn-tmpl-delay"
            value={draft.delayHours}
            onChange={(e) => patch({ delayHours: Number(e.target.value) })}
          >
            {DELAYS.map((d) => <option key={d.hours} value={d.hours}>{d.label}</option>)}
          </select>
        </label>
        <button type="button" className="cta" disabled={saving || !!problem} title={problem || ''} onClick={save}>
          {saving ? 'Saving…' : editingId ? 'Save changes' : 'Save template'}
        </button>
      </div>
      {problem && <div className="img-hint" style={{ marginTop: 10 }}>{problem}</div>}
      {editingId && (
        <div className="img-hint" style={{ marginTop: 10 }}>
          Editing an existing template.{' '}
          <button type="button" className="tmpl-tgl" onClick={cancel}>Cancel</button>
        </div>
      )}

      <table className="hist" style={{ marginTop: 12 }}>
        <thead>
          <tr>
            <th>Template</th>
            <th>Shows</th>
            <th className="num">Sends</th>
            <th>Trigger</th>
            <th>Delay</th>
            <th>Status</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {templates === null && (
            <tr><td className="empty" colSpan={7}>Loading templates…</td></tr>
          )}
          {templates?.length === 0 && (
            <tr><td className="empty" colSpan={7}>No templates yet.</td></tr>
          )}
          {(templates || []).map((t) => (
            <tr key={t._id}>
              <td><b>{t.name}</b></td>
              <td className="tmpl-sends">
                {t.image && <img src={t.image} alt="" />}
                {formatLabel(t.format)} · {placementLabel(t.placement)}
                {(t.title || t.body) && ` · “${(t.title || t.body).slice(0, 40)}”`}
              </td>
              <td className="num">{num(t.seen)}</td>
              <td>{triggerLabel(t.trigger)}</td>
              <td>{delayLabel(t.delayHours)}</td>
              <td><span className={`status ${t.active ? 'live' : 'ended'}`}>{t.active ? 'On' : 'Off'}</span></td>
              <td className="num">
                <span className="row-acts">
                  <button
                    type="button"
                    className={`tmpl-tgl ${editingId === t._id ? 'on' : ''}`.trim()}
                    disabled={busyId === t._id}
                    onClick={() => edit(t)}
                  >
                    {editingId === t._id ? 'Editing' : 'Edit'}
                  </button>
                  <button
                    type="button"
                    className="tmpl-tgl"
                    disabled={busyId === t._id}
                    onClick={() => onToggle(t)}
                  >
                    {t.active ? 'Turn off' : 'Turn on'}
                  </button>
                  <button
                    type="button"
                    className="tmpl-tgl danger"
                    disabled={busyId === t._id}
                    onClick={() => {
                      if (window.confirm(`Delete “${t.name}” and its ${num(t.seen)} impressions? This cannot be undone.`)) {
                        onDelete(t);
                      }
                    }}
                  >
                    Delete
                  </button>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Templates;
